import {
  ArrowLeftRight,
  Building2,
  Check,
  Languages,
  LogOut,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useLogout, useSwitchOrganization } from "@/auth/session";
import { useSession } from "@/auth/session-context";
import { LANGUAGES, currentLanguage } from "@/i18n/i18n";
import { paths } from "@/routes/paths";
import { Avatar, AvatarFallback, AvatarImage } from "@punto-firma/ui/components/avatar";
import { Badge } from "@punto-firma/ui/components/badge";
import { Button } from "@punto-firma/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@punto-firma/ui/components/dropdown-menu";

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");

/**
 * Menú de usuario: quién soy y en qué organización, cambiar de organización
 * (si tengo varias), idioma, Plataforma (si soy de Punto) y salir.
 *
 * Al cambiar de organización, la sesión cambia de token
 * (`POST /v1/auth/switch-organization`), se descarta la caché y se vuelve a
 * Inicio.
 */
export const UserMenu = () => {
  const { t, i18n } = useTranslation(["nav", "common"]);
  const { me, role, isPlatformAdmin, currentOrganization } = useSession();
  const logout = useLogout();
  const switchOrganization = useSwitchOrganization();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="gap-2 px-2"
          aria-label={t("nav:userMenu")}
        >
          <span className="hidden text-sm text-muted-foreground sm:inline">
            {me.user.email}
          </span>
          <Avatar size="md">
            {me.user.pictureUrl ? (
              <AvatarImage src={me.user.pictureUrl} alt="" />
            ) : null}
            <AvatarFallback>
              {initials(me.user.name ?? me.user.email)}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="space-y-1 font-normal">
          <p className="truncate font-medium">
            {me.user.name ?? me.user.email}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {me.user.email}
          </p>
          {currentOrganization ? (
            <p className="truncate pt-1 text-sm">{currentOrganization.name}</p>
          ) : null}
          <div className="flex flex-wrap gap-1 pt-1">
            {role ? (
              <Badge variant="secondary">{t(`common:roles.${role}`)}</Badge>
            ) : null}
            {isPlatformAdmin ? (
              <Badge variant="secondary">{t("nav:platformAdmin")}</Badge>
            ) : null}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {me.organizations.length > 1 ? (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              className="gap-2"
              disabled={switchOrganization.isPending}
            >
              <ArrowLeftRight className="size-4" aria-hidden />
              {t("nav:organizationSwitcher")}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-64">
              {me.organizations.map((organization) => {
                const isCurrent = organization.id === currentOrganization?.id;
                return (
                  <DropdownMenuItem
                    key={organization.id}
                    disabled={isCurrent}
                    onSelect={() => switchOrganization.mutate(organization.id)}
                    className="gap-2"
                  >
                    <Check
                      className={isCurrent ? "size-4" : "size-4 opacity-0"}
                      aria-hidden
                    />
                    <span className="flex-1 truncate">{organization.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {t(`common:roles.${organization.role}`)}
                    </span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ) : null}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="gap-2">
            <Languages className="size-4" aria-hidden />
            {t("nav:language")}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={currentLanguage()}
              onValueChange={(language) => void i18n.changeLanguage(language)}
            >
              {LANGUAGES.map((language) => (
                <DropdownMenuRadioItem
                  key={language}
                  value={language}
                  lang={language}
                >
                  {t(`common:languages.${language}`)}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        {isPlatformAdmin ? (
          <DropdownMenuItem asChild className="gap-2">
            <Link to={paths.platform}>
              <Building2 className="size-4" aria-hidden />
              {t("nav:platform")}
            </Link>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => logout.mutate()}
          disabled={logout.isPending}
          className="gap-2"
        >
          <LogOut className="size-4" aria-hidden />
          {t("nav:logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
